/**
 * DataTable — typed list view (moved from apps/admin-panel 0.53.0; same props, so every page that
 * used it upgrades in place).
 *
 * Desktop: a table in a white rounded card; sortable headers are real buttons; a clickable row
 * also answers Enter / Space and can be marked selected (`selectedKey`).
 * Under 640px every row is a stacked card: the column flagged `cardTitle` (else the first column)
 * is the card heading, every other cell is "label: value". Done in CSS from data-label, so there is
 * one DOM for both and nothing to keep in step.
 */

import { useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';

export interface DataTableColumn<TRow> {
  /** Header label (also the label in phone card mode). */
  header: string;
  /** Optional width (px or %). */
  width?: string | number;
  /** Cell renderer. */
  cell: (row: TRow) => ReactNode;
  align?: 'left' | 'center' | 'right';
  /** When present the header sorts: unsorted -> ascending -> descending -> unsorted. Null/undefined sort last. */
  sortKey?: (row: TRow) => string | number | null | undefined;
  /** Phone card mode: this column is the card's heading. Default: the first column. */
  cardTitle?: boolean;
  /** Phone card mode: leave this cell out of the card (e.g. a duplicate of the heading). */
  hideInCard?: boolean;
}

export interface DataTableProps<TRow> {
  columns: ReadonlyArray<DataTableColumn<TRow>>;
  rows: ReadonlyArray<TRow>;
  rowKey: (row: TRow) => string;
  /** Row click (also Enter / Space on the focused row). */
  onRowClick?: (row: TRow) => void;
  /** Rendered in place of the table when rows is empty. */
  emptyState?: ReactNode;
  initialSort?: { columnIndex: number; dir: 'asc' | 'desc' };
  /** Highlights the row being edited. */
  selectedKey?: string | null;
  /** Accessible name of the table. */
  caption?: string;
}

type SortState = { columnIndex: number; dir: 'asc' | 'desc' } | null;

export function sortRows<TRow>(rows: ReadonlyArray<TRow>, columns: ReadonlyArray<DataTableColumn<TRow>>, sort: SortState): ReadonlyArray<TRow> {
  if (!sort) return rows;
  const col = columns[sort.columnIndex];
  if (!col?.sortKey) return rows;
  const dir = sort.dir === 'asc' ? 1 : -1;
  const key = col.sortKey;
  const indexed = rows.map((r, i) => ({ r, i, k: key(r) }));
  indexed.sort((a, b) => {
    const an = a.k === null || a.k === undefined;
    const bn = b.k === null || b.k === undefined;
    if (an && bn) return a.i - b.i;
    if (an) return 1;
    if (bn) return -1;
    if (typeof a.k === 'number' && typeof b.k === 'number') {
      return a.k === b.k ? a.i - b.i : (a.k < b.k ? -1 : 1) * dir;
    }
    const av = String(a.k).toLowerCase();
    const bv = String(b.k).toLowerCase();
    return av === bv ? a.i - b.i : (av < bv ? -1 : 1) * dir;
  });
  return indexed.map((x) => x.r);
}

export function DataTable<TRow>({
  columns,
  rows,
  rowKey,
  onRowClick,
  emptyState,
  initialSort,
  selectedKey,
  caption,
}: DataTableProps<TRow>): JSX.Element {
  const [sort, setSort] = useState<SortState>(initialSort ?? null);
  const sortedRows = useMemo(() => sortRows(rows, columns, sort), [rows, columns, sort]);
  const titleIndex = Math.max(0, columns.findIndex((c) => c.cardTitle));

  const onHeaderClick = (i: number): void => {
    if (!columns[i]?.sortKey) return;
    setSort((prev) => {
      if (!prev || prev.columnIndex !== i) return { columnIndex: i, dir: 'asc' };
      if (prev.dir === 'asc') return { columnIndex: i, dir: 'desc' };
      return null;
    });
  };

  const onRowKey = (e: KeyboardEvent<HTMLTableRowElement>, row: TRow): void => {
    // Only when the row itself has focus: Enter on a button inside the row is that button's.
    if (!onRowClick || e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onRowClick(row);
    }
  };

  if (rows.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }
  return (
    <div className="data-table__wrap ps-table__wrap">
      <table className="data-table ps-table">
        {caption && <caption className="ps-visually-hidden">{caption}</caption>}
        <thead>
          <tr>
            {columns.map((c, i) => {
              const sortable = !!c.sortKey;
              const active = sort?.columnIndex === i;
              const dir = active ? sort!.dir : null;
              return (
                <th
                  key={i}
                  scope="col"
                  style={{ width: c.width, textAlign: c.align ?? 'left' }}
                  aria-sort={sortable ? (dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : 'none') : undefined}
                >
                  {sortable ? (
                    <button type="button" className="ps-table__sort" onClick={() => onHeaderClick(i)}>
                      {c.header}
                      <span className="data-table__sort-indicator" aria-hidden="true">
                        {dir === 'asc' ? ' ▲' : dir === 'desc' ? ' ▼' : ' ⇅'}
                      </span>
                    </button>
                  ) : (
                    c.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row) => {
            const key = rowKey(row);
            const selected = selectedKey != null && selectedKey === key;
            const cls = [
              onRowClick ? 'data-table__row--clickable' : '',
              selected ? 'ps-table__row--selected' : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <tr
                key={key}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onKeyDown={onRowClick ? (e) => onRowKey(e, row) : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                aria-current={selected ? 'true' : undefined}
                className={cls || undefined}
              >
                {columns.map((c, i) => {
                  const isTitle = i === titleIndex;
                  const tdCls = [isTitle ? 'ps-table__card-title' : '', c.hideInCard && !isTitle ? 'ps-table__card-hide' : '']
                    .filter(Boolean)
                    .join(' ');
                  return (
                    <td
                      key={i}
                      style={{ textAlign: c.align ?? 'left' }}
                      data-label={isTitle ? undefined : c.header}
                      className={tdCls || undefined}
                    >
                      <div className="ps-table__cell">{c.cell(row)}</div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
